import React from "react";
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Platform,
} from "react-native";
import { GlassBackdrop } from "./glass";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import {
  HomeIcon,
  LibraryIcon,
  ControlsIcon,
  StatsIcon,
  MeIcon,
  TabIconProps,
} from "./icons/TabIcons";
import { colors, fontFamily } from "../theme";

export type ParentTabKey = "home" | "library" | "controls" | "stats" | "me";

type Props = {
  active: ParentTabKey;
  onChange: (key: ParentTabKey) => void;
};

// ── Tab config ───────────────────────────────────────────────────────────────
const TABS: { key: ParentTabKey; label: string }[] = [
  { key: "home", label: "Home" },
  { key: "library", label: "Library" },
  { key: "controls", label: "Controls" },
  { key: "stats", label: "Stats" },
  { key: "me", label: "Me" },
];

// The designer's own set, one vector per tab — identical on every platform.
// This used to be SF Symbols on iOS with lucide standing in for Android and
// web, so the bar drew different glyphs depending on where you ran it.
const ICONS: Record<ParentTabKey, React.ComponentType<TabIconProps>> = {
  home: HomeIcon,
  library: LibraryIcon,
  controls: ControlsIcon,
  stats: StatsIcon,
  me: MeIcon,
};

const ACTIVE_COLOR = colors.primary;
// Unselected tabs use the app's warm brown ink, not a grey. A neutral grey read
// as washed-out against the warm glass (and only just cleared contrast); the
// brown belongs to the same family as the body text and the orange accent.
const INACTIVE_COLOR = colors.ink2; // #6B5A48

// ─────────────────────────────────────────────────────────────────────────────
// Liquid-glass tab bar (iOS 26 look): a capsule that FLOATS clear of the screen
// edges so page content passes behind and refracts through it.
//
// The look is layered, not a single colour:
//   1. GlassBackdrop       — real backdrop blur; the glass itself.
//   2. sheen               — soft white top-down wash, so the slab reads as lit
//                            from above rather than uniformly frosted.
//   3. rim (hairline)      — bright 1px specular edge that catches the light.
//   4. drop shadow         — separates the capsule from the content beneath.
// The selected tab gets its own inner "lens": a brighter tinted capsule with
// its own rim, which is what makes selection feel like glass-on-glass.
//
// Android and web have no equivalent backdrop blur, so GlassBackdrop falls back
// to a near-opaque slab — same geometry, just less translucency.
// ─────────────────────────────────────────────────────────────────────────────
export function ParentTabBar({ active, onChange }: Props) {
  const insets = useSafeAreaInsets();

  return (
    <View
      // Sits above the home indicator, inset from the sides so it visibly
      // floats. pointerEvents stays default — the bar is interactive.
      style={[
        styles.wrap,
        { paddingBottom: Math.max(insets.bottom, 10), },
      ]}
    >
      <View style={styles.capsule}>
        <GlassBackdrop blurAmount={22} fallbackColor="rgba(255,250,243,0.96)" />

        {/* Lit-from-above wash + specular rim. */}
        <View style={styles.sheen} pointerEvents="none" />
        <View style={styles.rim} pointerEvents="none" />

        <View style={styles.row}>
          {TABS.map((t) => {
            const on = t.key === active;
            const iconColor = on ? ACTIVE_COLOR : INACTIVE_COLOR;
            const Icon = ICONS[t.key];
            return (
              <TouchableOpacity
                key={t.key}
                onPress={() => onChange(t.key)}
                style={styles.item}
                activeOpacity={0.7}
                accessibilityRole="button"
                accessibilityState={{ selected: on }}
                accessibilityLabel={t.label}
              >
                {/* The selected "lens" — brighter glass with its own edge. */}
                {on && <View style={styles.lens} pointerEvents="none" />}
                <Icon size={on ? 25 : 23} color={iconColor} />
                <Text
                  style={[
                    styles.label,
                    { color: iconColor, fontWeight: on ? "800" : "600" },
                  ]}
                >
                  {t.label}
                </Text>
              </TouchableOpacity>
            );
          })}
        </View>
      </View>
    </View>
  );
}

const RADIUS = 30;

// Space a scrolling screen must reserve at the bottom so its last row isn't
// trapped under the floating bar. Covers the capsule (~74), the wrap's top
// padding and the home-indicator inset. Every screen that renders this bar
// should use it as its scroll contentContainer paddingBottom.
export const TAB_BAR_CLEARANCE = 124;

const styles = StyleSheet.create({
  wrap: {
    // Floats OVER the page so content scrolls behind the glass and refracts
    // through it — an in-flow bar would just blur its own solid backdrop.
    // The screens already reserve ~88px of bottom padding in their scroll
    // content, so nothing ends up unreachable underneath.
    position: "absolute",
    left: 0,
    right: 0,
    bottom: 0,
    paddingHorizontal: 12,
    paddingTop: 6,
    backgroundColor: "transparent",
  },
  capsule: {
    borderRadius: RADIUS,
    overflow: "hidden",
    // Float: a soft, wide shadow reads as depth without a hard edge.
    shadowColor: "#2A1A0A",
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.16,
    shadowRadius: 22,
    elevation: 18,
    // Faint tint so the glass keeps the app's warmth over dark content.
    backgroundColor: Platform.OS === "ios" ? "rgba(255,250,243,0.55)" : undefined,
  },
  sheen: {
    ...StyleSheet.absoluteFillObject,
    borderTopLeftRadius: RADIUS,
    borderTopRightRadius: RADIUS,
    backgroundColor: "rgba(255,255,255,0.28)",
    // Only the upper half is washed, so the slab has a light direction.
    bottom: "45%",
  },
  rim: {
    ...StyleSheet.absoluteFillObject,
    borderRadius: RADIUS,
    borderWidth: StyleSheet.hairlineWidth * 1.5,
    borderColor: "rgba(255,255,255,0.75)",
  },
  row: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 6,
    paddingVertical: 9,
  },
  item: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 7,
    paddingHorizontal: 2,
    borderRadius: 20,
  },
  lens: {
    ...StyleSheet.absoluteFillObject,
    borderRadius: 20,
    backgroundColor: "rgba(255,255,255,0.62)",
    borderWidth: StyleSheet.hairlineWidth * 1.5,
    borderColor: "rgba(255,255,255,0.9)",
    shadowColor: ACTIVE_COLOR,
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.18,
    shadowRadius: 6,
  },
  label: {
    fontFamily,
    fontSize: 10.5,
    letterSpacing: 0.1,
    marginTop: 3,
  },
});

export default ParentTabBar;
